import { canvasDeltaToPdfDelta } from "../../../../utils/coordinateUtils";
import { handleFieldClipboardShortcut } from "./fieldClipboard";

export const handleKeyDown = (
  e,
  currentProcessingFile,
  setCurrentProcessingFile,
  typingToolActive,
  setReverseDeleted,
  setCurrCursor,
  selectToolActive,
  focusedField,
  setTypingToolActive,
  setSelectToolActive,
  currCursor,
  setFocusedField,
  setHighlightFields,
  expandToolBox,
  setExpandToolBox,
  activeEditorTool,
  setActiveEditorTool,
) => {
  if (!currentProcessingFile?.document?.tempFields?.currentDocument.base64File)
    return;

  const shortcutKey = e.key?.toLowerCase();

  if ((e.ctrlKey || e.metaKey) && ["c", "v"].includes(shortcutKey)) {
    const handled = handleFieldClipboardShortcut(
      e,
      currentProcessingFile,
      setCurrentProcessingFile,
      typingToolActive,
    );

    if (handled && shortcutKey === "v") {
      setCurrCursor("default");
      setTypingToolActive(false);
      setSelectToolActive(true);
      setFocusedField(null);
      setActiveEditorTool?.("edit");
    }

    // When editing text, allow the browser's normal copy/paste.
    return;
  }

  const target = e.target;

  const isTypingInField =
    (target instanceof HTMLInputElement && !target.readOnly) ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable;

  if (
    document.activeElement.tagName === "INPUT" &&
    !currentProcessingFile?.document?.tempFields?.selectedFormField
  ) {
    const isToolActive = Object.values(
      currentProcessingFile?.document?.tempFields?.formFieldTools,
    ).some((field) => field.active);
    if (isToolActive) {
      if (document.activeElement && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
    } else {
      return; // Do nothing if an input field is focused
    }
  }
  if (e.key === "Delete" && !typingToolActive && !isTypingInField) {
    let fieldsToRemove = [];

    if (currentProcessingFile?.document?.tempFields?.selectedFormField) {
      fieldsToRemove = [
        currentProcessingFile?.document?.tempFields?.selectedFormField,
        ...currentProcessingFile?.document?.tempFields?.selectedExtraFields.filter(
          (field) =>
            field.name !==
            currentProcessingFile?.document?.tempFields?.selectedFormField.name,
        ),
      ];
    } else {
      fieldsToRemove =
        currentProcessingFile?.document?.tempFields?.selectedExtraFields;
    }

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFieldTools: (() => {
            const updatedTools = {
              ...prevFile.document.tempFields.formFieldTools,
            };

            fieldsToRemove.forEach((field) => {
              const matchingKey = Object.keys(
                prevFile.document.tempFields.formFieldTools,
              ).find((key) =>
                field.name.startsWith(
                  prevFile.document.tempFields.formFieldTools[key].name,
                ),
              );

              if (
                matchingKey &&
                prevFile.document.tempFields.formFieldTools[matchingKey]
                  .disabled
              ) {
                updatedTools[matchingKey] = {
                  ...updatedTools[matchingKey],
                  disabled: false, // ✅ Re-enable tool
                };
              }
            });

            return updatedTools;
          })(),
        },
      },
    }));

    //? MAINTAIN SIGNING ORDERS
    let replacements = [];
    let fieldsToRemoveWithB = [...fieldsToRemove];

    fieldsToRemove.forEach((field) => {
      if (field.name.startsWith("notary_seal_a")) {
        // Replace notary_seal_a with notary_seal_b (if exists)
        const sealB =
          currentProcessingFile?.document?.tempFields?.formFields.find((f) =>
            f.name.startsWith("notary_seal_b"),
          );
        if (sealB) {
          replacements.push({
            ...sealB,
            name: `notary_seal_a_${Date.now().toString().slice(-4)}`,
          });
          fieldsToRemoveWithB.push(sealB);
        }
      }

      if (field.name.startsWith("notary_sign_a")) {
        // Replace notary_sign_a with notary_sign_b (if exists)
        const signB =
          currentProcessingFile?.document?.tempFields?.formFields.find((f) =>
            f.name.startsWith("notary_sign_b"),
          );
        if (signB) {
          replacements.push({
            ...signB,
            name: `notary_sign_a_${Date.now().toString().slice(-4)}`,
          });
          fieldsToRemoveWithB.push(signB);
        }
      }

      //? HANDLE SIGNING FIELDS
      if (field.name.startsWith("sign_a")) {
        const signB =
          currentProcessingFile?.document?.tempFields?.formFields.find((f) =>
            f.name.startsWith("sign_b"),
          );
        const signC =
          currentProcessingFile?.document?.tempFields?.formFields.find((f) =>
            f.name.startsWith("sign_c"),
          );

        if (signB) {
          replacements.push({
            ...signB,
            name: `sign_a_${Date.now().toString().slice(-4)}`,
          });
          fieldsToRemoveWithB.push(signB);
        }

        if (signC && signB) {
          replacements.push({
            ...signC,
            name: `sign_b_${Date.now().toString().slice(-4)}`,
          });
          fieldsToRemoveWithB.push(signC);
        }
      }

      if (field.name.startsWith("sign_b")) {
        const signC =
          currentProcessingFile?.document?.tempFields?.formFields.find((f) =>
            f.name.startsWith("sign_c"),
          );

        if (signC) {
          replacements.push({
            ...signC,
            name: `sign_b_${Date.now().toString().slice(-4)}`,
          });
          fieldsToRemoveWithB.push(signC);
        }
      }
    });

    //? Save deleted fields for undo
    setReverseDeleted(fieldsToRemove);

    //? Single State Update (Merging setFormFieldTools, setFormFields, and setCurrentProcessingFile)
    setCurrentProcessingFile((prevFile) => {
      // ✅ Remove fields but add replacements if needed
      let updatedFields = prevFile.document.tempFields.formFields.filter(
        (field) =>
          !fieldsToRemoveWithB.some((removed) => removed.name === field.name),
      );

      //Added this to remove block_Id
      const deletedBlockIds = new Set(
        fieldsToRemoveWithB
          .filter(
            (field) =>
              field?.type === "PDFRectangle" &&
              field?.name?.startsWith("block__"),
          )
          .map((field) => field?.block_id)
          .filter(Boolean),
      );

      if (deletedBlockIds.size > 0) {
        updatedFields = updatedFields.map((field) =>
          deletedBlockIds.has(field?.block_id)
            ? { ...field, block_id: "", block_label: "" }
            : field,
        );
      }

      // ✅ Add replacements if available
      updatedFields = [...updatedFields, ...replacements];

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFieldTools: {
              ...prevFile.document.tempFields.formFieldTools,
              ...(prevFile.document.tempFields.formFields.some((f) =>
                f.name.startsWith("notary_seal_b"),
              ) && {
                notarySeal_a: {
                  ...prevFile.document.tempFields.formFieldTools.notarySeal_a,
                  disabled: true,
                },
                notarySeal_b: {
                  ...prevFile.document.tempFields.formFieldTools.notarySeal_b,
                  disabled: false,
                  values: [],
                },
              }),
              ...(prevFile.document.tempFields.formFields.some((f) =>
                f.name.startsWith("notary_sign_b"),
              ) && {
                notarySignature_a: {
                  ...prevFile.document.tempFields.formFieldTools
                    .notarySignature_a,
                  disabled: true,
                },
                notarySignature_b: {
                  ...prevFile.document.tempFields.formFieldTools
                    .notarySignature_b,
                  disabled: false,
                  values: [],
                },
              }),
            },
            formFields: updatedFields,
            selectedFormField: null,
            selectedExtraFields: [],
            reverseDeleted: fieldsToRemove, // Store deleted fields for undo
          },
        },
      };
    });

    //? Remove focus from active element
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }

  // Handle Ctrl+Z for undoing field deletions
  if (e.ctrlKey && e.key === "z" && !focusedField) {
    e.preventDefault();

    // Check if there are deleted fields to restore
    const deletedFields =
      currentProcessingFile?.document?.tempFields?.reverseDeleted || [];

    if (deletedFields.length > 0) {
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFields: [
              ...prevFile.document.tempFields.formFields,
              ...deletedFields,
            ],
            reverseDeleted: [], // Clear the deleted fields after restoring
          },
        },
      }));

      // Clear the reverseDeleted state
      setReverseDeleted([]);
    }
  }

  if (e.key === "Escape") {
    const isToolActive = Object.values(
      currentProcessingFile?.document?.tempFields?.formFieldTools,
    ).some((field) => field.active);

    if (
      isToolActive ||
      (currentProcessingFile?.document?.tempFields?.selectedFormField &&
        selectToolActive)
    ) {
      setCurrCursor("default");

      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            formFieldTools: isToolActive
              ? Object.keys(prevFile.document.tempFields.formFieldTools).reduce(
                  (acc, key) => {
                    acc[key] = {
                      ...prevFile.document.tempFields.formFieldTools[key],
                      active: false,
                      values: [],
                    };
                    return acc;
                  },
                  {},
                )
              : prevFile.document.tempFields.formFieldTools,
            selectedFormField:
              currentProcessingFile?.document?.tempFields?.selectedFormField &&
              selectToolActive
                ? null
                : prevFile.document.tempFields.selectedFormField,
            selectedExtraFields:
              currentProcessingFile?.document?.tempFields?.selectedFormField &&
              selectToolActive
                ? []
                : prevFile.document.tempFields.selectedExtraFields,
          },
        },
      }));
    }

    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }
  if (e.key === "t" && !focusedField && !isTypingInField) {
    e.preventDefault();

    if (typingToolActive) {
      setTypingToolActive(false);
      setSelectToolActive(true);
      setActiveEditorTool("edit");
    } else {
      setTypingToolActive(true);
      setSelectToolActive(false);
      setActiveEditorTool("hand");
    }

    const activeTool = Object.values(
      currentProcessingFile?.document?.tempFields?.formFieldTools,
    ).find((field) => field.active);

    if (
      activeTool ||
      currentProcessingFile?.document?.tempFields?.selectedFormField ||
      currentProcessingFile?.document?.tempFields?.selectedExtraFields?.length >
        0
    ) {
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: prevFile.document.tempFields.selectedFormField
              ? null
              : prevFile.document.tempFields.selectedFormField,
            selectedExtraFields:
              prevFile.document.tempFields.selectedExtraFields?.length > 0
                ? []
                : prevFile.document.tempFields.selectedExtraFields,

            // ✅ Reset formFieldTools if activeTool exists
            formFieldTools: activeTool
              ? Object.keys(prevFile.document.tempFields.formFieldTools).reduce(
                  (acc, key) => {
                    acc[key] = {
                      ...prevFile.document.tempFields.formFieldTools[key],
                      active: false,
                      values: [],
                    };
                    return acc;
                  },
                  {},
                )
              : prevFile.document.tempFields.formFieldTools, // Keep unchanged if no activeTool
          },
        },
      }));
    }

    if (currCursor !== "default") {
      setCurrCursor("default");
    }

    if (focusedField) {
      setFocusedField(null);
    }

    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }
  }

  if (e.key === "h" && !focusedField && !isTypingInField) {
    setHighlightFields((prev) => !prev);
  }

  if (
    ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) &&
    !isTypingInField
  ) {
    if (!currentProcessingFile?.document?.tempFields?.selectedFormField) return;
    const movementStep = e.ctrlKey ? 1 : 5;

    let screenDx = 0;
    let screenDy = 0;

    if (e.key === "ArrowUp") screenDy = -movementStep;
    if (e.key === "ArrowDown") screenDy = movementStep;
    if (e.key === "ArrowLeft") screenDx = -movementStep;
    if (e.key === "ArrowRight") screenDx = movementStep;

    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument || {};
    const { x: dx, y: dy } = canvasDeltaToPdfDelta(
      screenDx,
      screenDy,
      currentDocument.viewport,
      currentDocument.scaleFactor,
      true,
    );

    if (dx !== 0 || dy !== 0) {
      e.preventDefault();
    }

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: prevFile.document.tempFields.formFields.map((f) =>
            prevFile.document.tempFields.selectedFormField?.name === f.name ||
            prevFile.document.tempFields.selectedExtraFields?.some(
              (sf) => sf.name === f.name,
            )
              ? { ...f, x: f.x + dx, y: f.y + dy }
              : f,
          ),

          // ✅ Update selectedFormField if it exists
          selectedFormField: prevFile.document.tempFields.selectedFormField
            ? {
                ...prevFile.document.tempFields.selectedFormField,
                x: prevFile.document.tempFields.selectedFormField.x + dx,
                y: prevFile.document.tempFields.selectedFormField.y + dy,
              }
            : prevFile.document.tempFields.selectedFormField, // Keep as is if null/undefined

          // ✅ Update selectedExtraFields if they exist
          selectedExtraFields: prevFile.document.tempFields.selectedExtraFields
            ? prevFile.document.tempFields.selectedExtraFields.map((field) => ({
                ...field,
                x: field.x + dx,
                y: field.y + dy,
              }))
            : prevFile.document.tempFields.selectedExtraFields, // Keep as is if null/undefined
        },
      },
    }));
  }

  //? ///////////////////////////////////////////////////////////////
  const field_tool_keys = ["g", "n", "s"];
  if (
    field_tool_keys.includes(e.key) &&
    !e.ctrlKey &&
    !e.metaKey &&
    !focusedField &&
    !isTypingInField
  ) {
    e.preventDefault();

    if (typingToolActive) {
      setTypingToolActive(false);
      setSelectToolActive(true);
    }

    if (!expandToolBox) {
      setExpandToolBox(true);
    }

    if (
      currentProcessingFile?.document?.tempFields?.selectedFormField ||
      currentProcessingFile?.document?.tempFields?.selectedExtraFields?.length >
        0 ||
      currentProcessingFile?.document?.tempFields?.formFieldTools
    ) {
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: prevFile.document.tempFields.selectedFormField
              ? null
              : prevFile.document.tempFields.selectedFormField,
            selectedExtraFields:
              prevFile.document.tempFields.selectedExtraFields?.length > 0
                ? []
                : prevFile.document.tempFields.selectedExtraFields,

            // ✅ Update formFieldTools logic
            formFieldTools: ((prevTools) => {
              // ✅ Get all tools in the same group
              const toolsInGroup = Object.entries(prevTools).filter(
                ([_, tool]) => tool.group === e.key,
              );

              if (toolsInGroup.length === 0) {
                if (currCursor !== "default") setCurrCursor("default");
                return prevTools;
              }

              // ✅ Only consider tools that are NOT disabled
              const enabledTools = toolsInGroup.filter(
                ([_, tool]) => !tool.disabled,
              );

              if (enabledTools.length === 0) {
                if (currCursor !== "default") setCurrCursor("default");
                return prevTools;
              }

              // ✅ Find currently active tool index
              const activeIndex = enabledTools.findIndex(
                ([_, tool]) => tool.active,
              );
              const nextIndex =
                activeIndex === -1
                  ? 0
                  : (activeIndex + 1) % enabledTools.length;

              const nextActiveKey = enabledTools[nextIndex][0]; // Key of the next tool

              const updatedTools = {};

              for (const key in prevTools) {
                updatedTools[key] = {
                  ...prevTools[key],
                  active: key === nextActiveKey,
                };
              }

              if (currCursor !== "crosshair") setCurrCursor("crosshair");

              return updatedTools;
            })(prevFile.document.tempFields.formFieldTools), // Apply the function to prevFile.formFieldTools
          },
        },
      }));
    }
  }
};
